import { useQuery } from "@tanstack/react-query";
import { type StoryboardData } from "@shared/schema";
import { StoryPanel } from "@/components/StoryPanel";
import { FixedCTAButton } from "@/components/FixedCTAButton";
import ProductModal from "@/components/ProductModal";
import { Skeleton } from "@/components/ui/skeleton";
import { useState } from "react";
import { Link } from "wouter";
import { Helmet } from "react-helmet-async";

export default function Home() {
  const { data: storyboardData, isLoading } = useQuery<StoryboardData>({
    queryKey: ["/storyboard.json"],
  });

  const [productOpen, setProductOpen] = useState(false);

  // Hero section: Horizontal side-by-side on desktop, compact fit on mobile, no emojis, wish hidden on mobile
  const HeroSection = () => (
    <section
      className="min-h-screen w-full flex flex-col items-center justify-center px-4 relative overflow-hidden py-6 md:py-16 bg-transparent"
      aria-label="Ganesh Chaturthi Hero"
    >
      {/* Logo — top left */}
      <div className="absolute top-3 left-3 md:top-6 md:left-6 z-10 select-none">
        <img
          src="/images/ganjidrinks-logo.png"
          alt="Ganji Drinks Logo"
          className="h-16 md:h-28 w-auto drop-shadow transition-all duration-300"
          fetchPriority="high"
        />
      </div>

      {/* Side-by-Side Flex Container */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 md:gap-12 w-full max-w-6xl z-[2] relative pt-16 md:pt-6">
        {/* Left Column: Heading & Subtitle & Greeting (Wish hidden on mobile per request) */}
        <div className="w-full md:w-1/2 flex flex-col items-center md:items-start text-center md:text-left">
          <h1
            className="select-none font-bold tracking-wide text-[#61290C]"
            style={{
              fontFamily: "'Permanent Marker', cursive",
              fontSize: "clamp(2.8rem, 6.5vw, 5.5rem)",
              lineHeight: "1.05",
              textShadow: "1px 1px 2px rgba(243, 234, 218, 0.9)",
            }}
          >
            Ganji
          </h1>

          <p
            className="text-xl md:text-4xl mt-1 font-bold text-[#7C4424] tracking-wide mb-4 md:mb-6"
            style={{
              fontFamily: "'Permanent Marker', cursive",
              textShadow: "1px 1px 2px rgba(243, 234, 218, 0.9)",
            }}
          >
            A Sip of Home
          </p>

          {/* Festive Greeting Box — Hidden on Mobile, Shown on Desktop, NO Emojis */}
          <div 
            className="hidden md:block p-5 rounded-2xl border-2 border-[#61290C] shadow-lg max-w-md backdrop-blur-md"
            style={{ backgroundColor: "rgba(220, 196, 156, 0.88)" }}
          >
            <span 
              className="inline-block px-3 py-1 text-xs font-bold uppercase rounded-full mb-2 bg-[#61290C] text-[#F3EADA]"
              style={{ fontFamily: "'Patrick Hand', cursive" }}
            >
              Happy Ganesh Chaturthi
            </span>
            <p 
              className="text-base md:text-lg font-bold text-[#1E0404] leading-relaxed"
              style={{ fontFamily: "'Patrick Hand', cursive" }}
            >
              May Lord Ganesha, the Vighnaharta, remove all obstacles from your path and bless your home with health, peace, and sweet prosperity!
            </p>
          </div>
        </div>

        {/* Right Column: Hero Poster Image */}
        <div className="w-full md:w-1/2 flex justify-center">
          <div className="relative max-w-xs sm:max-w-sm md:max-w-lg w-full shadow-2xl rounded-2xl overflow-hidden border-3 md:border-4 border-[#61290C] bg-[#F3EADA]">
            <img
              src="/images/ganesh/ganesh-hero-poster.jpg"
              alt="Happy Ganesh Chaturthi Hasichitara Poster"
              className="w-full h-auto block select-none"
            />
          </div>
        </div>
      </div>
    </section>
  );

  // Story Chapters Stream — Sticky Stacked Animations (Mobile & Desktop visible completely, NO Emojis)
  const HasichitaraStoryStream = () => {
    const chapters = [
      {
        id: "chapter-1",
        num: "01",
        eyebrow: "CHAPTER 01 • CREATION",
        title: "Born of Earth and Divine Will",
        quote: "Formed from natural turmeric earth and divine breath, Ganesha's journey begins with loyalty and unwavering courage.",
        copy: "Goddess Parvati, seeking a steadfast guardian for her sacred quarters, fashioned a radiant young boy from turmeric paste and earth, breathing life and courage into his heart. Standing guard at her doorway with a staff in hand, young Ganesha remained firm in his duty, refusing passage even to Lord Shiva, demonstrating absolute devotion to his mother's command.",
        image: "/images/ganesh/chapter1-creation.jpg",
        highlights: ["Parvati's Earth Crafting", "Turmeric & Soil Life", "Steadfast Doorway Guard"],
        cardBg: "rgba(243, 234, 218, 0.98)",
        borderColor: "#C28A42",
        badgeBg: "#C28A42",
        badgeText: "#1E0404",
        quoteBg: "#DCC49C",
        highlightBg: "#CCAC8C",
        highlightText: "#1E0404",
      },
      {
        id: "chapter-2",
        num: "02",
        eyebrow: "CHAPTER 02 • WISDOM",
        title: "The Symbol of Supreme Intellect",
        quote: "With broad ears to hear every prayer and a gentle trunk to guide seekers, Lord Ganesha became Vighnaharta.",
        copy: "Moved by Ganesha's ultimate devotion and bravery, Lord Shiva restored him with the noble head of an elephant—a symbol of supreme wisdom, deep listening, and royal grace. Shiva bestowed upon him the sacred titles of Prathama Pujya (the first deity worshipped before any venture) and Vighnaharta (the remover of all obstacles on life's path).",
        image: "/images/ganesh/chapter2-vighnaharta.jpg",
        highlights: ["Prathama Pujya Title", "Mooshika Mouse Companion", "Remover of Obstacles"],
        cardBg: "rgba(220, 196, 156, 0.98)",
        borderColor: "#61290C",
        badgeBg: "#61290C",
        badgeText: "#F3EADA",
        quoteBg: "#F3EADA",
        highlightBg: "#B49474",
        highlightText: "#1E0404",
      },
      {
        id: "chapter-3",
        num: "03",
        eyebrow: "CHAPTER 03 • CELEBRATION",
        title: "Welcoming the Lord of Blessings Home",
        quote: "For ten sacred days during Bhadrapada, homes and hearts glow with festive warmth and rangoli garlands.",
        copy: "Every year during the auspicious Hindu month of Bhadrapada, Ganesh Chaturthi commemorates the birth and earthly arrival of Lord Ganesha. Families clean their homes, draw intricate geometric rangolis at their thresholds, hang fresh mango leaf garlands, and invite eco-friendly clay idols of Ganesha into their hearts and homes.",
        image: "/images/ganesh/chapter3-celebration.jpg",
        highlights: ["Mango Leaf Garlands", "Geometric Floor Rangoli", "Eco-friendly Clay Idol"],
        cardBg: "rgba(243, 234, 218, 0.98)",
        borderColor: "#7C4424",
        badgeBg: "#7C4424",
        badgeText: "#F3EADA",
        quoteBg: "#CCAC8C",
        highlightBg: "#C28A42",
        highlightText: "#1E0404",
      },
      {
        id: "chapter-4",
        num: "04",
        eyebrow: "CHAPTER 04 • MODAKA",
        title: "Sweet Offerings of Joy and Devotion",
        quote: "Shaped like a lotus bud, the twenty-one modakas symbolize the sweet prize of spiritual enlightenment.",
        copy: "Central to the festivities is Ganesha's beloved sweet—the Modaka. Shaped like a closed lotus bud, this steamed delicacy of rice flour, fresh coconut, and jaggery symbolizes the sweet prize of spiritual enlightenment and supreme knowledge, bringing families together in joy and gratitude.",
        image: "/images/ganesh/chapter4-modaka.jpg",
        highlights: ["Lotus Bud Shape", "21 Modaka Offering", "Sacred Altar Diyas"],
        cardBg: "rgba(204, 172, 140, 0.98)",
        borderColor: "#925A32",
        badgeBg: "#925A32",
        badgeText: "#F3EADA",
        quoteBg: "#F3EADA",
        highlightBg: "#DCC49C",
        highlightText: "#61290C",
      },
      {
        id: "chapter-5",
        num: "05",
        eyebrow: "CHAPTER 05 • VISARJAN",
        title: "From Earth to Water, Returning to Nature",
        quote: "As Ganesha returns to the waters, He leaves behind peace, wisdom, and the promise to return again next year.",
        copy: "The celebration culminates in grand Visarjan processions. Accompanied by beating dhol drums, dancing, and joyous chants, the clay idols are reverently immersed into rivers and lakes. This poignant ritual reminds humanity that all life originates from mother earth and gracefully returns to it, continuing the natural cycle of renewal.",
        image: "/images/ganesh/chapter5-visarjan.jpg",
        highlights: ["Dhol Drum Processions", "River Flower Boat", "Nature Renewal Cycle"],
        cardBg: "rgba(243, 234, 218, 0.98)",
        borderColor: "#A47C54",
        badgeBg: "#A47C54",
        badgeText: "#F3EADA",
        quoteBg: "#DCC49C",
        highlightBg: "#C28A42",
        highlightText: "#1E0404",
      },
    ];

    return (
      <section className="py-8 md:py-20 px-3 md:px-4 relative z-10 select-none bg-transparent">
        <div className="max-w-5xl mx-auto">
          {/* Story Cards (Sticky Stack Animations on both mobile & desktop) */}
          <div className="relative space-y-6 md:space-y-12">
            {chapters.map((chap, idx) => {
              const isEven = idx % 2 === 0;
              return (
                <div
                  key={chap.id}
                  id={chap.id}
                  className="sticky top-14 md:top-24 rounded-2xl md:rounded-3xl shadow-2xl p-4 md:p-10 transition-all duration-300 mb-8 md:mb-12 backdrop-blur-md"
                  style={{
                    backgroundColor: chap.cardBg,
                    borderColor: chap.borderColor,
                    borderWidth: "2px",
                    borderStyle: "solid",
                    zIndex: idx + 1,
                  }}
                >
                  <div className="hasichitara-border-teeth mb-4 md:mb-6 opacity-80" aria-hidden="true" />

                  <div className={`flex flex-col ${isEven ? "lg:flex-row" : "lg:flex-row-reverse"} items-center gap-5 md:gap-10`}>
                    {/* Chapter Artwork Image Column */}
                    <div className="w-full lg:w-1/2 flex-shrink-0">
                      <div 
                        className="relative rounded-xl md:rounded-2xl overflow-hidden shadow-lg group border-2 max-h-48 sm:max-h-64 lg:max-h-none"
                        style={{ borderColor: chap.borderColor }}
                      >
                        <img
                          src={chap.image}
                          alt={chap.title}
                          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                        />
                        <div 
                          className="absolute top-2 left-2 md:top-3 md:left-3 text-[10px] md:text-xs font-bold px-2.5 py-1 rounded-full shadow-md"
                          style={{
                            backgroundColor: chap.badgeBg,
                            color: chap.badgeText,
                            fontFamily: "'Patrick Hand', cursive",
                          }}
                        >
                          {chap.eyebrow}
                        </div>
                      </div>
                    </div>

                    {/* Text Column */}
                    <div className="w-full lg:w-1/2 flex flex-col justify-center text-left">
                      {/* Header Row: Large Chapter Number & Eyebrow Badge */}
                      <div className="flex items-center justify-between gap-3 mb-2 md:mb-3 border-b-2 border-[#61290C]/20 pb-2 md:pb-3">
                        <span 
                          className="px-2.5 py-0.5 md:px-3 md:py-1 text-[11px] md:text-sm font-bold uppercase tracking-wider rounded-full"
                          style={{
                            backgroundColor: chap.badgeBg,
                            color: chap.badgeText,
                            fontFamily: "'Patrick Hand', cursive",
                          }}
                        >
                          {chap.eyebrow}
                        </span>
                        <span 
                          className="text-2xl md:text-5xl font-bold text-[#61290C]/40"
                          style={{ fontFamily: "'Permanent Marker', cursive" }}
                        >
                          #{chap.num}
                        </span>
                      </div>

                      {/* Chapter Title */}
                      <h3
                        className="text-xl md:text-4xl font-bold mb-2 md:mb-3 text-[#61290C] leading-tight"
                        style={{ fontFamily: "'Permanent Marker', cursive" }}
                      >
                        {chap.title}
                      </h3>

                      {/* Pull Quote Box */}
                      <div 
                        className="p-3 md:p-4 rounded-xl border-l-4 shadow-sm mb-3 md:mb-4"
                        style={{
                          backgroundColor: chap.quoteBg,
                          borderColor: chap.borderColor,
                        }}
                      >
                        <p 
                          className="text-xs md:text-lg font-bold italic text-[#1E0404]"
                          style={{ fontFamily: "'Patrick Hand', cursive" }}
                        >
                          "{chap.quote}"
                        </p>
                      </div>

                      {/* Narrative Copy */}
                      <p
                        className="text-xs md:text-lg leading-relaxed text-[#61290C] font-bold mb-4 md:mb-5"
                        style={{ fontFamily: "'Patrick Hand', cursive" }}
                      >
                        {chap.copy}
                      </p>

                      {/* Hasichitara Highlight Badges (NO Emojis, bullet used) */}
                      <div className="flex flex-wrap gap-1.5 md:gap-2 pt-1">
                        {chap.highlights.map((item) => (
                          <span
                            key={item}
                            className="px-2.5 py-1 rounded-full text-[10px] md:text-sm font-bold border shadow-sm"
                            style={{
                              backgroundColor: chap.highlightBg,
                              color: chap.highlightText,
                              borderColor: chap.borderColor,
                              fontFamily: "'Patrick Hand', cursive",
                            }}
                          >
                            • {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="hasichitara-border-teeth mt-6 md:mt-8 opacity-80" aria-hidden="true" />
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-transparent">
        <HeroSection />
        <HasichitaraStoryStream />
        <div className="grid md:grid-cols-2 gap-0">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-screen p-8">
              <Skeleton className="w-full h-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!storyboardData) {
    return (
      <div className="min-h-screen flex flex-col bg-transparent">
        <HeroSection />
        <HasichitaraStoryStream />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-[#61290C] mb-4" data-testid="text-error">
              Story Not Available
            </h1>
            <p className="text-[#7C4424]">
              The storyboard content could not be loaded. Please try again later.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Ganji Drinks — Happy Ganesh Chaturthi | A Sip of Home</title>
        <meta
          name="description"
          content="Celebrate Ganesh Chaturthi with Ganji Drinks. Discover the story of Lord Ganesha in authentic Hasichitara folk art style."
        />
      </Helmet>

      {/* Main Page Layout */}
      <div className="relative min-h-screen flex flex-col justify-between bg-transparent">
        <HeroSection />
        <HasichitaraStoryStream />

        {/* Section: Actual 12 Split-Screen Storyboard Panels */}
        <div id="storyboard-panels" className="relative z-10 py-8 md:py-12 bg-transparent">
          <div className="relative">
            {storyboardData.panels.map((panel: any, index: number) => (
              <StoryPanel key={panel.number} panel={panel} index={index} />
            ))}
          </div>
        </div>

        {/* Fixed CTA Button */}
        <FixedCTAButton
          button={storyboardData.fixed_button}
          onClick={() => setProductOpen(true)}
        />
        <ProductModal open={productOpen} onOpenChange={setProductOpen} />

        {/* Last Section: Responsive Footer (Resized & Padded for Mobile View) */}
        <div className="relative z-10 px-3 md:px-4 py-8 md:py-16 flex items-center justify-center">
          <footer
            className="w-full max-w-5xl mx-auto border-2 border-[#61290C]/30 rounded-2xl md:rounded-3xl shadow-2xl p-5 md:p-16 text-center backdrop-blur-md pb-28 md:pb-36"
            style={{
              backgroundColor: "rgba(243, 234, 218, 0.88)",
            }}
          >
            <div className="max-w-4xl mx-auto">
              <h2
                className="text-3xl md:text-6xl font-bold mb-2 md:mb-4 text-[#61290C]"
                style={{
                  fontFamily: "'Permanent Marker', cursive",
                  lineHeight: "1.2",
                }}
              >
                Ganpati Bappa Morya!
              </h2>
              <p
                className="text-lg md:text-3xl font-bold mb-4 md:mb-6 text-[#7C4424]"
                style={{
                  fontFamily: "'Permanent Marker', cursive",
                  lineHeight: "1.4",
                }}
              >
                May Lord Ganesha Bless Every Home With Wisdom & Harmony
              </p>

              <p
                className="text-xs md:text-xl mb-6 md:mb-10 text-[#61290C] font-bold max-w-lg mx-auto leading-relaxed"
                style={{
                  fontFamily: "'Patrick Hand', cursive",
                }}
              >
                Join our journey of tradition and celebration. Follow us on social media for stories and updates!
              </p>

              <div className="my-6 md:my-8">
                <div
                  className="inline-flex items-center justify-center gap-4 md:gap-6 px-5 py-2.5 md:px-8 md:py-3.5 rounded-full shadow-xl border border-[#F3EADA]/30"
                  style={{
                    backgroundColor: "#61290C",
                    color: "#F3EADA",
                  }}
                >
                  <a
                    href="https://x.com/ganjidrinks"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit our X profile"
                    className="transition-opacity hover:opacity-90"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="currentColor" className="md:w-6 md:h-6">
                      <path d="M18.243 3H21l-7.119 8.168L22 21h-4.757l-6.087-7.01L7.5 21H3l7.52-8.64L2 3h4.757l5.522 6.361L18.243 3z" />
                    </svg>
                  </a>
                  <a
                    href="https://www.linkedin.com/company/its-ganjidrinks/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit our LinkedIn page"
                    className="transition-opacity hover:opacity-90"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="currentColor" className="md:w-6 md:h-6">
                      <path d="M4.983 3.5a2.5 2.5 0 110 5 2.5 2.5 0 010-5zM3 8.75h4v12.75H3V8.75zm7.5 0h3.833v1.74h.054c.534-1.013 1.84-2.081 3.788-2.081 4.05 0 4.8 2.664 4.8 6.132v6.959h-4V15.39c0-1.612-.03-3.683-2.244-3.683-2.246 0-2.59 1.752-2.59 3.565v6.228h-3.641V8.75z" />
                    </svg>
                  </a>
                  <a
                    href="https://whatsapp.com/channel/0029VbBbmYKAInPhJvL4Iy3s"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit our WhatsApp channel"
                    className="transition-opacity hover:opacity-90"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="currentColor" className="md:w-6 md:h-6">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.472-.148-.671.15-.198.297-.767.966-.94 1.164-.173.198-.347.223-.644.074-.297-.149-1.255-.463-2.39-1.476-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.074-.149-.671-1.611-.919-2.206-.242-.579-.487-.5-.671-.51-.173-.01-.372-.01-.571-.01-.198 0-.521.074-.793.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.879 1.213 3.077.149.198 2.1 3.2 5.082 4.487.711.306 1.267.489 1.699.625.713.226 1.362.194 1.875.118.571-.085 1.758-.718 2.006-1.412.248-.694.248-1.289.173-1.412-.074-.124-.272-.198-.57-.347z" />
                      <path d="M11.999 2a9.9 9.9 0 00-8.997 13.887L2 22l6.212-1.631A9.9 9.9 0 0012 21.9c5.452 0 9.9-4.448 9.9-9.9S17.452 2 12 2zm0 17.82a7.92 7.92 0 01-4.128-1.18l-.296-.175-3.689.969.985-3.593-.192-.307a7.92 7.92 0 117.32 4.286z" />
                    </svg>
                  </a>
                  <a
                    href="https://www.instagram.com/ganjidrinks/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit our Instagram page"
                    className="transition-opacity hover:opacity-90"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="md:w-6 md:h-6">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                    </svg>
                  </a>
                </div>
              </div>

              {/* Legal Links */}
              <div className="mt-6 md:mt-8 flex flex-wrap justify-center gap-3 md:gap-6">
                <Link
                  href="/founder"
                  className="text-xs md:text-base transition-all hover:opacity-80 text-[#61290C] font-bold"
                  style={{ fontFamily: "'Patrick Hand', cursive" }}
                >
                  Founder
                </Link>
                <Link
                  href="/about"
                  className="text-xs md:text-base transition-all hover:opacity-80 text-[#61290C] font-bold"
                  style={{ fontFamily: "'Patrick Hand', cursive" }}
                >
                  About Us
                </Link>
                <Link
                  href="/blog"
                  className="text-xs md:text-base transition-all hover:opacity-80 text-[#61290C] font-bold"
                  style={{ fontFamily: "'Patrick Hand', cursive" }}
                >
                  Blog
                </Link>
                <Link
                  href="/privacy-policy"
                  className="text-xs md:text-base transition-all hover:opacity-80 text-[#61290C] font-bold"
                  style={{ fontFamily: "'Patrick Hand', cursive" }}
                >
                  Privacy Policy
                </Link>
                <Link
                  href="/terms-of-use"
                  className="text-xs md:text-base transition-all hover:opacity-80 text-[#61290C] font-bold"
                  style={{ fontFamily: "'Patrick Hand', cursive" }}
                >
                  Terms of Use
                </Link>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </>
  );
}
